import * as THREE from 'three';

export interface Health {
  hp: number;
  maxHp: number;
  invulnerableT: number;
  flashT: number;
  knockbackVel: THREE.Vector3;
  knockbackResist: number;
}

export function createHealth(maxHp: number, knockbackResist = 0): Health {
  return { hp: maxHp, maxHp, invulnerableT: 0, flashT: 0, knockbackVel: new THREE.Vector3(), knockbackResist };
}

export const FLASH_DURATION = 0.12;
const KNOCKBACK_DECAY_RATE = 9; // 1/s-ish — knockback bleeds off over a few frames, not instantly and not forever

export interface DamageOptions {
  /** World-space XZ direction to push the target — need not be normalized. */
  knockbackDir: THREE.Vector3;
  knockbackForce: number;
  /** i-frame window granted to the TARGET after this specific hit (ms). Omit for none. */
  invulnerableAfterMs?: number;
}

/**
 * THE single damage choke point (PLAN-CITY §0.7, carried over from Classic
 * gotcha #14) — every hit path in S02 (player bullets -> enemy, enemy
 * contact/lob -> player, the super blast) must call this, never mutate
 * `.hp` directly. Returns true exactly once, on the call that crosses hp to
 * <=0, so callers can trigger death-only effects (hitstop, kill callbacks)
 * without a separate "is this the killing blow" check of their own.
 */
export function applyDamage(health: Health, amount: number, opts: DamageOptions): boolean {
  if (health.invulnerableT > 0 || health.hp <= 0) return false;
  const wasAlive = health.hp > 0;
  health.hp = Math.max(0, health.hp - amount);
  health.flashT = FLASH_DURATION;
  const pushForce = opts.knockbackForce * (1 - health.knockbackResist);
  const dir = opts.knockbackDir.lengthSq() > 1e-8 ? opts.knockbackDir.clone().setY(0).normalize() : new THREE.Vector3();
  health.knockbackVel.copy(dir).multiplyScalar(pushForce);
  if (opts.invulnerableAfterMs) health.invulnerableT = opts.invulnerableAfterMs / 1000;
  return wasAlive && health.hp <= 0;
}

/** Decays i-frames, hit-flash, and knockback velocity. Callers apply `knockbackVel` into their own movement resolution — this module only owns its decay. */
export function tickHealth(health: Health, dt: number): void {
  if (health.invulnerableT > 0) health.invulnerableT = Math.max(0, health.invulnerableT - dt);
  if (health.flashT > 0) health.flashT = Math.max(0, health.flashT - dt);
  health.knockbackVel.multiplyScalar(Math.max(0, 1 - dt * KNOCKBACK_DECAY_RATE));
}

/**
 * Hitstop-lite: a single GLOBAL timer (not per-entity) that main.ts's tick()
 * reads to decide whether to skip advancing AI/projectiles for a frame.
 * Armed ONLY on a kill (never a mere hit — Classic gotcha #45's "bounded on
 * purpose" lesson: an unbounded hitstop source clips badly the instant
 * enemies are dense) and deliberately does NOT pause the player controller
 * or camera — freezing the one thing the player is actively driving would
 * read as lag, not impact. A brief world-only flinch on a kill is the
 * "-lite" the plan's own wording calls for.
 */
let hitstopRemaining = 0;

export function armHitstop(ms: number): void {
  hitstopRemaining = Math.max(hitstopRemaining, ms / 1000);
}

/** Call once per tick. Returns true if the world-sim (AI/projectiles) should skip this tick. */
export function tickHitstop(dt: number): boolean {
  if (hitstopRemaining <= 0) return false;
  hitstopRemaining = Math.max(0, hitstopRemaining - dt);
  return true;
}
